// Real-world country borders (neighbours). Only includes countries in countryToCellMapping.
// Used for graph-based distance, "attack only neighbours", etc.
// Source: standard geographic borders; keys must match countryToCellMapping.js.

import { countryToCellMap, cellToCountryMap, getCountryFromCellId } from './countryToCellMapping';

/** Country name -> array of neighbour country names (each neighbour is in our map) */
export const COUNTRY_NEIGHBOURS = {
  // North America
  'United States': ['Canada', 'Mexico'],
  'Canada': ['United States'],
  'Mexico': ['United States', 'Guatemala', 'Belize'],
  'Guatemala': ['Mexico', 'Belize', 'El Salvador', 'Honduras'],
  'Belize': ['Mexico', 'Guatemala'],
  'El Salvador': ['Guatemala', 'Honduras'],
  'Honduras': ['Guatemala', 'El Salvador', 'Nicaragua'],
  'Nicaragua': ['Honduras', 'Costa Rica'],
  'Costa Rica': ['Nicaragua', 'Panama'],
  'Panama': ['Costa Rica', 'Colombia'],
  'Cuba': [],
  'Jamaica': [],
  'Haiti': ['Dominican Republic'],
  'Dominican Republic': ['Haiti'],

  // South America
  'Brazil': ['Venezuela', 'Guyana', 'Suriname', 'French Guiana', 'Uruguay', 'Argentina', 'Paraguay', 'Bolivia', 'Peru', 'Colombia'],
  'Argentina': ['Chile', 'Bolivia', 'Paraguay', 'Brazil', 'Uruguay'],
  'Chile': ['Peru', 'Bolivia', 'Argentina'],
  'Peru': ['Ecuador', 'Colombia', 'Brazil', 'Bolivia'],
  'Colombia': ['Venezuela', 'Brazil', 'Peru', 'Ecuador', 'Panama'],
  'Venezuela': ['Colombia', 'Brazil', 'Guyana'],
  'Ecuador': ['Colombia', 'Peru'],
  'Bolivia': ['Peru', 'Brazil', 'Paraguay', 'Argentina', 'Chile'],
  'Paraguay': ['Bolivia', 'Brazil', 'Argentina'],
  'Uruguay': ['Brazil', 'Argentina'],
  'Guyana': ['Venezuela', 'Suriname', 'Brazil'],
  'Suriname': ['Guyana', 'French Guiana', 'Brazil'],
  'French Guiana': ['Suriname', 'Brazil'],

  // Europe
  'United Kingdom': ['Ireland'],
  'France': ['Spain', 'Belgium', 'Luxembourg', 'Germany', 'Switzerland', 'Italy'],
  'Germany': ['Denmark', 'Poland', 'Czech Republic', 'Austria', 'Switzerland', 'France', 'Luxembourg', 'Belgium', 'Netherlands'],
  'Italy': ['France', 'Switzerland', 'Austria', 'Slovenia'],
  'Spain': ['France', 'Portugal'],
  'Poland': ['Germany', 'Czech Republic', 'Slovakia', 'Ukraine', 'Belarus', 'Lithuania', 'Russia'],
  'Ukraine': ['Poland', 'Slovakia', 'Hungary', 'Romania', 'Moldova', 'Belarus', 'Russia'],
  'Russia': ['Norway', 'Finland', 'Estonia', 'Latvia', 'Lithuania', 'Poland', 'Belarus', 'Ukraine', 'Georgia', 'Azerbaijan', 'Kazakhstan', 'China', 'Mongolia', 'North Korea'],
  'Russian Federation': ['Norway', 'Finland', 'Estonia', 'Latvia', 'Lithuania', 'Poland', 'Belarus', 'Ukraine', 'Georgia', 'Azerbaijan', 'Kazakhstan', 'China', 'Mongolia', 'North Korea'],
  'Sweden': ['Norway', 'Finland'],
  'Norway': ['Russia', 'Sweden', 'Finland'],
  'Finland': ['Norway', 'Sweden', 'Russia'],
  'Denmark': ['Germany'],
  'Netherlands': ['Germany', 'Belgium'],
  'Belgium': ['France', 'Germany', 'Luxembourg', 'Netherlands'],
  'Switzerland': ['France', 'Germany', 'Austria', 'Italy'],
  'Austria': ['Germany', 'Czech Republic', 'Slovakia', 'Hungary', 'Slovenia', 'Italy', 'Switzerland'],
  'Czech Republic': ['Germany', 'Poland', 'Slovakia', 'Austria'],
  'Portugal': ['Spain'],
  'Greece': ['Albania', 'North Macedonia', 'Bulgaria', 'Turkey'],
  'Romania': ['Ukraine', 'Moldova', 'Hungary', 'Serbia', 'Bulgaria'],
  'Hungary': ['Slovakia', 'Ukraine', 'Romania', 'Serbia', 'Croatia', 'Slovenia', 'Austria'],
  'Bulgaria': ['Romania', 'Greece', 'North Macedonia', 'Serbia', 'Turkey'],
  'Croatia': ['Slovenia', 'Hungary', 'Serbia', 'Bosnia and Herzegovina'],
  'Serbia': ['Hungary', 'Romania', 'Bulgaria', 'North Macedonia', 'Kosovo', 'Montenegro', 'Bosnia and Herzegovina', 'Croatia'],
  'Ireland': ['United Kingdom'],
  'Iceland': [],
  'Turkey': ['Greece', 'Bulgaria', 'Georgia', 'Armenia', 'Azerbaijan', 'Iran', 'Iraq', 'Syria'],
  'Albania': ['Montenegro', 'Kosovo', 'North Macedonia', 'Greece'],
  'Armenia': ['Turkey', 'Georgia', 'Azerbaijan', 'Iran'],
  'Azerbaijan': ['Russia', 'Georgia', 'Armenia', 'Turkey', 'Iran'],
  'Belarus': ['Latvia', 'Lithuania', 'Poland', 'Ukraine', 'Russia'],
  'Bosnia and Herzegovina': ['Croatia', 'Serbia', 'Montenegro'],
  'Estonia': ['Latvia', 'Russia'],
  'Georgia': ['Russia', 'Turkey', 'Armenia', 'Azerbaijan'],
  'Latvia': ['Estonia', 'Lithuania', 'Belarus', 'Russia'],
  'Lithuania': ['Latvia', 'Belarus', 'Poland', 'Russia'],
  'Luxembourg': ['Belgium', 'Germany', 'France'],
  'Moldova': ['Ukraine', 'Romania'],
  'Montenegro': ['Croatia', 'Bosnia and Herzegovina', 'Serbia', 'Albania'],
  'North Macedonia': ['Serbia', 'Bulgaria', 'Greece', 'Albania', 'Kosovo'],
  'Slovakia': ['Poland', 'Ukraine', 'Hungary', 'Czech Republic', 'Austria'],
  'Slovenia': ['Austria', 'Italy', 'Hungary', 'Croatia'],
  'Cyprus': [],
  'Malta': [],
  'Kosovo': ['Serbia', 'Montenegro', 'North Macedonia', 'Albania'],

  // Asia
  'China': ['Mongolia', 'Russia', 'North Korea', 'Vietnam', 'Laos', 'Myanmar', 'India', 'Bhutan', 'Nepal', 'Pakistan', 'Afghanistan', 'Tajikistan', 'Kyrgyzstan', 'Kazakhstan'],
  'India': ['Pakistan', 'China', 'Nepal', 'Bhutan', 'Bangladesh', 'Myanmar'],
  'Japan': [],
  'South Korea': ['North Korea'],
  'Indonesia': ['Malaysia', 'Papua New Guinea'],
  'Thailand': ['Myanmar', 'Laos', 'Cambodia', 'Malaysia'],
  'Vietnam': ['China', 'Laos', 'Cambodia'],
  'Philippines': [],
  'Malaysia': ['Thailand', 'Indonesia', 'Brunei', 'Singapore'],
  'Singapore': ['Malaysia'],
  'Myanmar': ['India', 'China', 'Bangladesh', 'Laos', 'Thailand'],
  'Cambodia': ['Thailand', 'Laos', 'Vietnam'],
  'Laos': ['China', 'Myanmar', 'Thailand', 'Vietnam', 'Cambodia'],
  'Bangladesh': ['India', 'Myanmar'],
  'Pakistan': ['Afghanistan', 'India', 'Iran', 'China'],
  'Afghanistan': ['Turkmenistan', 'Uzbekistan', 'Tajikistan', 'China', 'Pakistan', 'Iran'],
  'Iran': ['Turkey', 'Iraq', 'Pakistan', 'Afghanistan', 'Turkmenistan', 'Armenia', 'Azerbaijan'],
  'Iraq': ['Turkey', 'Iran', 'Syria', 'Jordan', 'Saudi Arabia', 'Kuwait'],
  'Saudi Arabia': ['Jordan', 'Iraq', 'Kuwait', 'Qatar', 'United Arab Emirates', 'Oman', 'Yemen'],
  'United Arab Emirates': ['Saudi Arabia', 'Oman'],
  'Israel': ['Lebanon', 'Syria', 'Jordan', 'Palestine'],
  'Jordan': ['Israel', 'Palestine', 'Syria', 'Iraq', 'Saudi Arabia'],
  'Lebanon': ['Syria', 'Israel'],
  'Syria': ['Turkey', 'Iraq', 'Jordan', 'Israel', 'Lebanon'],
  'Kazakhstan': ['Russia', 'China', 'Kyrgyzstan', 'Uzbekistan', 'Turkmenistan'],
  'Uzbekistan': ['Kazakhstan', 'Turkmenistan', 'Tajikistan', 'Kyrgyzstan', 'Afghanistan'],
  'Mongolia': ['Russia', 'China'],
  'Nepal': ['China', 'India'],
  'Sri Lanka': [],
  'Brunei': ['Malaysia'],
  'Bhutan': ['China', 'India'],
  'Kyrgyzstan': ['Kazakhstan', 'China', 'Tajikistan', 'Uzbekistan'],
  'Tajikistan': ['Afghanistan', 'China', 'Kyrgyzstan', 'Uzbekistan'],
  'Turkmenistan': ['Kazakhstan', 'Uzbekistan', 'Afghanistan', 'Iran'],
  'Taiwan': [],
  'Palestine': ['Israel', 'Jordan'],
  'North Korea': ['China', 'Russia', 'South Korea'],

  // Africa
  'Egypt': ['Libya', 'Sudan', 'Israel', 'Palestine'],
  'Libya': ['Tunisia', 'Algeria', 'Niger', 'Chad', 'Sudan', 'Egypt'],
  'Algeria': ['Libya', 'Mali', 'Mauritania', 'Morocco', 'Tunisia', 'Niger', 'Western Sahara'],
  'Morocco': ['Algeria', 'Western Sahara'],
  'Tunisia': ['Libya', 'Algeria'],
  'Sudan': ['Egypt', 'Libya', 'Chad', 'Central African Republic', 'South Sudan', 'Ethiopia', 'Eritrea'],
  'Ethiopia': ['Sudan', 'South Sudan', 'Kenya', 'Somalia', 'Djibouti', 'Eritrea'],
  'Kenya': ['Ethiopia', 'Somalia', 'Tanzania', 'Uganda', 'South Sudan'],
  'Tanzania': ['Kenya', 'Uganda', 'Rwanda', 'Burundi', 'Zambia', 'Malawi', 'Mozambique', 'Democratic Republic of the Congo'],
  'South Africa': ['Namibia', 'Botswana', 'Zimbabwe', 'Mozambique', 'Lesotho'],
  'Nigeria': ['Niger', 'Chad', 'Cameroon', 'Benin'],
  'Ghana': ['Burkina Faso', 'Ivory Coast', 'Togo'],
  'Congo': ['Cameroon', 'Central African Republic', 'Democratic Republic of the Congo'],
  'Angola': ['Democratic Republic of the Congo', 'Zambia', 'Namibia'],
  'Zambia': ['Tanzania', 'Malawi', 'Mozambique', 'Zimbabwe', 'Botswana', 'Namibia', 'Angola', 'Democratic Republic of the Congo'],
  'Zimbabwe': ['Zambia', 'Botswana', 'South Africa', 'Mozambique'],
  'Madagascar': [],
  'Mozambique': ['Tanzania', 'Malawi', 'Zambia', 'Zimbabwe', 'South Africa'],
  'Cameroon': ['Nigeria', 'Chad', 'Central African Republic', 'Congo', 'Equatorial Guinea', 'Gabon'],
  'Ivory Coast': ['Mali', 'Burkina Faso', 'Ghana', 'Liberia', 'Guinea'],
  'Senegal': ['Mauritania', 'Mali', 'Guinea', 'Guinea-Bissau'],
  'Mali': ['Mauritania', 'Algeria', 'Niger', 'Burkina Faso', 'Ivory Coast', 'Guinea', 'Senegal'],
  'Niger': ['Libya', 'Chad', 'Nigeria', 'Benin', 'Burkina Faso', 'Mali', 'Algeria'],
  'Chad': ['Libya', 'Sudan', 'Central African Republic', 'Cameroon', 'Nigeria', 'Niger'],
  'Somalia': ['Ethiopia', 'Kenya', 'Djibouti'],
  'Uganda': ['Kenya', 'South Sudan', 'Democratic Republic of the Congo', 'Rwanda', 'Tanzania'],
  'Rwanda': ['Uganda', 'Tanzania', 'Burundi', 'Democratic Republic of the Congo'],
  'Burundi': ['Rwanda', 'Tanzania', 'Democratic Republic of the Congo'],
  'Botswana': ['Namibia', 'Zimbabwe', 'South Africa', 'Zambia'],
  'Central African Republic': ['Chad', 'Sudan', 'South Sudan', 'Democratic Republic of the Congo', 'Congo', 'Cameroon'],
  'Democratic Republic of the Congo': ['Congo', 'Central African Republic', 'South Sudan', 'Uganda', 'Rwanda', 'Burundi', 'Tanzania', 'Zambia', 'Angola'],
  'Lesotho': ['South Africa'],
  'Malawi': ['Tanzania', 'Mozambique', 'Zambia'],
  'Namibia': ['Angola', 'Zambia', 'Botswana', 'South Africa'],
  'South Sudan': ['Sudan', 'Ethiopia', 'Kenya', 'Uganda', 'Democratic Republic of the Congo', 'Central African Republic'],
  'Western Sahara': ['Morocco', 'Mauritania', 'Algeria'],
  'Mauritania': ['Western Sahara', 'Algeria', 'Mali', 'Senegal'],
  'Burkina Faso': ['Mali', 'Niger', 'Benin', 'Togo', 'Ghana', 'Ivory Coast'],
  'Guinea': ['Guinea-Bissau', 'Senegal', 'Mali', 'Ivory Coast', 'Liberia', 'Sierra Leone'],
  'Sierra Leone': ['Guinea', 'Liberia'],
  'Liberia': ['Guinea', 'Ivory Coast', 'Sierra Leone'],
  'Togo': ['Burkina Faso', 'Ghana', 'Benin'],
  'Benin': ['Niger', 'Nigeria', 'Burkina Faso', 'Togo'],
  'Gabon': ['Equatorial Guinea', 'Cameroon', 'Congo'],
  'Equatorial Guinea': ['Cameroon', 'Gabon'],
  'Eritrea': ['Sudan', 'Ethiopia', 'Djibouti'],
  'Djibouti': ['Eritrea', 'Ethiopia', 'Somalia'],
  'Yemen': ['Saudi Arabia', 'Oman'],
  'Oman': ['United Arab Emirates', 'Saudi Arabia', 'Yemen'],
  'Qatar': ['Saudi Arabia'],
  'Kuwait': ['Iraq', 'Saudi Arabia'],
  'Bahrain': [],
  'Comoros': [],
  'Mauritius': [],
  'Seychelles': [],
  'Guinea-Bissau': ['Senegal', 'Guinea'],

  // Oceania
  'Australia': ['Papua New Guinea', 'Indonesia'],
  'New Zealand': [],
  'Papua New Guinea': ['Indonesia', 'Australia'],
  'Fiji': [],
  'New Caledonia': [],
  'Solomon Islands': [],
  'Vanuatu': [],
  'Samoa': [],
  'Tonga': [],
  'Micronesia': [],
  'Palau': [],

  // Other
  'Greenland': [],
};

/**
 * Get neighbour country names for a country.
 * @param {string} countryName - Country name (must match countryToCellMapping)
 * @returns {string[]} Neighbour country names
 */
export function getNeighbourCountries(countryName) {
  const neighbours = COUNTRY_NEIGHBOURS[countryName];
  if (neighbours === undefined) {
    return [];
  }
  return [...neighbours];
}

/**
 * Get neighbour cell IDs for a country (or cell ID).
 * @param {string|number} countryOrCellId - Country name or cell ID
 * @returns {number[]} Neighbour cell IDs
 */
export function getNeighbourCellIds(countryOrCellId) {
  const countryName = typeof countryOrCellId === 'number'
    ? cellToCountryMap[countryOrCellId]
    : countryOrCellId;
  if (!countryName || !COUNTRY_NEIGHBOURS.hasOwnProperty(countryName)) {
    return [];
  }
  const neighbourNames = getNeighbourCountries(countryName);
  return neighbourNames
    .map((name) => countryToCellMap[name])
    .filter((id) => id != null);
}

/**
 * Check if two countries/cells are neighbours (share a land border).
 * @param {string|number} a - Country name or cell ID
 * @param {string|number} b - Country name or cell ID
 * @returns {boolean}
 */
export function isNeighbour(a, b) {
  const cellIdA = typeof a === 'number' ? a : countryToCellMap[a];
  const cellIdB = typeof b === 'number' ? b : countryToCellMap[b];
  if (cellIdA == null || cellIdB == null) return false;
  if (cellIdA === cellIdB) return false;
  const neighboursOfA = getNeighbourCellIds(cellIdA);
  return neighboursOfA.includes(cellIdB);
}
